import { NavLink, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import {
    House, Users, Package, ShoppingCart, Truck, Wallet, Star, ChatCircleDots,
    Gear, ShieldCheck, ChartBar, FileText, Warning, Certificate, PlusCircle,
    MapPin, Heart, Storefront, IdentificationCard, ClipboardText, MagnifyingGlass,
    UserGear, Buildings, Money, ChartLine, Image, SignOut,
} from "@phosphor-icons/react";
import { BILLING_ENABLED } from "@/lib/marketplacePolicy";

const ROLE_LABEL = {
    admin: "Administrador", productor: "Productor",
    comprador: "Comprador", exportador: "Comprador (Exportador)",
    transportista: "Transportista",
};

const MENUS = {
    admin: [
        { to: "/admin", label: "Resumen", icon: House, end: true },
        { to: "/admin/usuarios", label: "Usuarios", icon: Users },
        { to: "/admin/productos", label: "Publicaciones", icon: Package },
        { to: "/admin/solicitudes", label: "Solicitudes de compra", icon: ShoppingCart },
        { to: "/admin/cargas", label: "Cargas y transporte", icon: Truck },
        { to: "/admin/verificaciones", label: "Verificaciones", icon: Certificate },
        { to: "/admin/mensajes", label: "Reportes de mensajes", icon: ChatCircleDots },
        { to: "/admin/catalogo-productos", label: "Catálogo de productos", icon: Package },
        { to: "/admin/catalogo-ubicaciones", label: "Catálogo de ubicaciones", icon: MapPin },
        { to: "/admin/precios-agropecuarios", label: "Precios del mercado", icon: ChartLine },
        { to: "/admin/imagenes-catalogo", label: "Subir imágenes", icon: Image },
        { to: "/admin/administrar-imagenes", label: "Administrar imágenes", icon: Image },
        { to: "/admin/auditoria", label: "Registro de auditoría", icon: ShieldCheck },
        { to: "/admin/configuracion", label: "Configuración", icon: Gear },
    ],
    productor: [
        { to: "/productor", label: "Resumen", icon: House, end: true },
        { to: "/productor/productos", label: "Mis productos", icon: Package },
        { to: "/productor/crear", label: "Crear producto", icon: PlusCircle },
        { to: "/productor/solicitudes", label: "Solicitudes de compradores", icon: ShoppingCart },
        { to: "/productor/ofertas", label: "Mis ofertas", icon: ClipboardText },
        { to: "/productor/pedidos", label: "Pedidos", icon: ShoppingCart },
        { to: "/productor/ventas", label: "Ventas", icon: ChartBar },
        { to: "/productor/envios", label: "Envíos", icon: Truck },
        { to: "/productor/ganancias", label: "Ganancias", icon: Wallet },
        { to: "/productor/mensajes", label: "Mensajes", icon: ChatCircleDots },
        { to: "/productor/resenas", label: "Reseñas", icon: Star },
        { to: "/productor/perfil", label: "Mi perfil", icon: IdentificationCard },
    ],
    comprador: [
        { to: "/comprador", label: "Resumen", icon: House, end: true },
        { to: "/marketplace", label: "Explorar productos", icon: MagnifyingGlass },
        { to: "/comprador/solicitudes", label: "Mis solicitudes", icon: ClipboardText },
        { to: "/comprador/ofertas", label: "Ofertas recibidas", icon: ShoppingCart },
        { to: "/comprador/pedidos", label: "Mis pedidos", icon: Package },
        { to: "/comprador/compras", label: "Mis compras", icon: ChartBar },
        { to: "/comprador/envios", label: "Envíos", icon: Truck },
        { to: "/comprador/gastos", label: "Gastos y facturas", icon: Money },
        { to: "/comprador/favoritos", label: "Favoritos", icon: Heart },
        { to: "/comprador/mensajes", label: "Mensajes", icon: ChatCircleDots },
        { to: "/comprador/resenas", label: "Reseñas", icon: Star },
        { to: "/comprador/perfil", label: "Mi perfil", icon: Buildings },
    ],
    transportista: [
        { to: "/transportista", label: "Resumen", icon: House, end: true },
        { to: "/transportista/cargas", label: "Cargas disponibles", icon: MagnifyingGlass },
        { to: "/transportista/ofertas", label: "Mis ofertas", icon: ClipboardText },
        { to: "/transportista/asignadas", label: "Cargas asignadas", icon: Package },
        { to: "/transportista/en-transito", label: "En tránsito", icon: Truck },
        { to: "/transportista/entregadas", label: "Entregas completadas", icon: MapPin },
        { to: "/transportista/ganancias", label: "Ganancias", icon: Wallet },
        { to: "/transportista/vehiculos", label: "Vehículos", icon: Truck },
        { to: "/transportista/conductores", label: "Conductores", icon: UserGear },
        { to: "/transportista/mensajes", label: "Mensajes", icon: ChatCircleDots },
        { to: "/transportista/resenas", label: "Reseñas", icon: Star },
        { to: "/transportista/perfil", label: "Mi perfil", icon: IdentificationCard },
    ],
};

export function DashboardShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [adminToolsOpen, setAdminToolsOpen] = useState(false);
    const [signingOut, setSigningOut] = useState(false);
    const signOut = async () => {
        if (signingOut) return;
        setSigningOut(true);
        try {
            const revokeServerSession = logout();
            navigate("/", { replace: true });
            await revokeServerSession;
        } finally {
            setSigningOut(false);
        }
    };
    const role = user?.role === "admin" || user?.roles?.includes("admin") ? "admin" : user?.role || "productor";
    const items = (MENUS[role] || []).filter(
        (item) => BILLING_ENABLED || item.to !== "/comprador/gastos"
    );
    const label = ROLE_LABEL[role] || role;
    const adminQuickLabels = {
        "/admin": "Panel",
        "/admin/usuarios": "Usuarios",
        "/admin/productos": "Anuncios",
        "/admin/mensajes": "Reportes",
    };
    return (
        <div className="container-page py-6" data-testid={`role-shell-${role}`}>
            {role === "admin" && (
                <section className="lg:hidden rounded-2xl border border-border bg-white mb-4 overflow-hidden" aria-label="Administración" data-testid="admin-mobile-navigation">
                    <div className="p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-sm font-extrabold text-[#2E7D32]">Panel de administración</div>
                            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button type="button" onClick={() => setAdminToolsOpen((open) => !open)}
                                aria-expanded={adminToolsOpen} aria-controls="admin-mobile-all-tools"
                                className="min-h-11 px-3 border border-border rounded-xl text-xs font-bold"
                                data-testid="admin-mobile-tools-toggle">
                                {adminToolsOpen ? "Ocultar" : "Herramientas"}
                            </button>
                            <button type="button" onClick={signOut} disabled={signingOut}
                                aria-label="Cerrar sesión de administrador" title="Cerrar sesión"
                                className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-xl border border-red-200 text-red-700 disabled:opacity-50"
                                data-testid="admin-mobile-logout">
                                <SignOut size={20} weight="bold" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                    <nav aria-label="Accesos rápidos de administración" className="grid grid-cols-4 gap-1.5 px-3 pb-3">
                        {items.filter((item) => adminQuickLabels[item.to]).map(({to, icon: Icon}) => (
                            <NavLink key={to} to={to} end={to === "/admin"} onClick={() => setAdminToolsOpen(false)}
                                className={({isActive}) => `flex flex-col items-center justify-center gap-1 min-h-[66px] rounded-xl px-1 text-[11px] font-semibold ${isActive ? "bg-[#2E7D32] text-white" : "bg-[#F3F7F2] text-[#2E7D32]"}`}
                                data-testid={`admin-mobile-shortcut-${to.split("/").pop()}`}>
                                <Icon size={21} weight="bold" aria-hidden="true" />
                                <span className="truncate max-w-full">{adminQuickLabels[to]}</span>
                            </NavLink>
                        ))}
                    </nav>
                    <nav id="admin-mobile-all-tools" aria-label="Todas las herramientas de administración"
                        className={`${adminToolsOpen ? "grid" : "hidden"} grid-cols-2 gap-2 p-3 pt-0 border-t border-border`}
                        data-testid="admin-mobile-all-tools">
                        {items.map(({to, icon: Icon, label, end}) => (
                            <NavLink key={to} to={to} end={end} onClick={() => setAdminToolsOpen(false)}
                                className={({isActive}) => `flex items-center gap-2 min-h-11 rounded-lg px-2.5 text-xs font-semibold ${isActive ? "bg-[#2E7D32] text-white" : "bg-secondary/70 text-foreground"}`}>
                                <Icon size={17} className="shrink-0" aria-hidden="true" />
                                <span>{label}</span>
                            </NavLink>
                        ))}
                    </nav>
                    <div className="border-t border-border px-3 py-2">
                        <button type="button" onClick={signOut} disabled={signingOut}
                            className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-red-700 min-h-11 disabled:opacity-50"
                            data-testid="admin-mobile-signout">
                            <SignOut size={18} aria-hidden="true" /> {signingOut ? "Cerrando sesión…" : "Cerrar sesión"}
                        </button>
                    </div>
                </section>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <aside className={role === "admin" ? "hidden lg:block lg:col-span-3" : "lg:col-span-3"}>
                    <div className="bg-white border border-border rounded-2xl p-4 mb-4">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{"Sesión activa"}</div>
                        <div className="mt-1 font-display font-bold truncate">{user?.name || user?.email}</div>
                        <div className="mt-1 inline-flex items-center gap-1 text-xs font-bold rounded-full bg-[#2E7D32]/10 text-[#2E7D32] px-2.5 py-1" data-testid="role-label">
                            <ShieldCheck size={12} weight="fill" /> {label}
                        </div>
                    </div>
                    <nav className="bg-white border border-border rounded-2xl p-2 flex flex-col gap-0.5" data-testid={`role-nav-${role}`}>
                        {items.map((it) => (
                            <NavLink key={it.to} to={it.to} end={it.end}
                                className={({ isActive }) =>
                                    `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition ${isActive
                                        ? "bg-[#2E7D32] text-white"
                                        : "text-foreground hover:bg-secondary"}`}
                                data-testid={`nav-${role}-${it.label.toLowerCase().replace(/\s/g, "-")}`}>
                                <it.icon size={16} weight="fill" /> {it.label}
                            </NavLink>
                        ))}
                        <button onClick={signOut} disabled={signingOut} className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-muted-foreground hover:bg-secondary mt-2 border-t border-border text-left" data-testid="nav-logout">
                            <SignOut size={16} /> {"Cerrar sesión"}
                        </button>
                    </nav>
                </aside>
                <main className="lg:col-span-9" data-testid={`role-main-${role}`}>{children}</main>
            </div>
        </div>
    );
}

export function KPICard({ label, value, hint, icon: Icon, testId }) {
    return (
        <div className="bg-white border border-border rounded-2xl p-5" data-testid={testId}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
                {Icon && <Icon size={18} weight="fill" className="text-[#2E7D32]" />}
            </div>
            <div className="mt-2 text-2xl font-extrabold tracking-tight">{value}</div>
            {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
        </div>
    );
}

export function EmptyState({ title, hint, icon: Icon = Storefront, cta, ctaTo, testId = "empty-state" }) {
    return (
        <div className="bg-white border border-dashed border-border rounded-2xl p-8 text-center" data-testid={testId}>
            <Icon size={36} weight="fill" className="mx-auto text-[#2E7D32] mb-3" />
            <div className="font-display font-bold text-lg">{title}</div>
            {hint && <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">{hint}</p>}
            {cta && ctaTo && (
                <Link to={ctaTo} className="btn-primary mt-4 h-10 px-4 text-sm inline-flex">{cta}</Link>
            )}
        </div>
    );
}
